/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBalanceScale,
  FaBuilding,
  FaCoins,
  FaFileAlt,
  FaFolderOpen,
  FaGavel,
  FaIdCard,
  FaPhoneAlt,
  FaRegFolderOpen,
  FaSearch,
  FaUserCircle,
} from "react-icons/fa";
import {
  Dossier,
  GlobalSearchResponse,
  fetchDossier,
  globalSearch,
} from "services/searchService";
import { errorHandler } from "utils/helper";

type IconComp = React.ComponentType<{ className?: string }>;
const Icon = {
  Search: FaSearch as unknown as IconComp,
  User: FaUserCircle as unknown as IconComp,
  IdCard: FaIdCard as unknown as IconComp,
  Phone: FaPhoneAlt as unknown as IconComp,
  Folder: FaFolderOpen as unknown as IconComp,
  RegFolder: FaRegFolderOpen as unknown as IconComp,
  Gavel: FaGavel as unknown as IconComp,
  Scale: FaBalanceScale as unknown as IconComp,
  Coins: FaCoins as unknown as IconComp,
  Building: FaBuilding as unknown as IconComp,
  File: FaFileAlt as unknown as IconComp,
};

// ---- helpers ----
const getInitials = (name?: string) => {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
};

const statusBadgeClass = (status?: string) => {
  const s = (status || "").toLowerCase();
  if (/(guilty|convict|approved|completed|final|granted)/.test(s))
    return "bg-green-100 text-green-800 border-green-200";
  if (/(rejected|denied|terminated|dismiss|acquit)/.test(s))
    return "bg-red-100 text-red-800 border-red-200";
  if (/(pending|submitted|assigned|on |progress|respond)/.test(s))
    return "bg-yellow-100 text-yellow-800 border-yellow-200";
  return "bg-gray-100 text-gray-800 border-gray-200";
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
        status,
      )}`}
    >
      {status}
    </span>
  );
};

const Field: React.FC<{
  label: string;
  value?: any;
  icon?: IconComp;
}> = ({ label, value, icon: IconC }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2">
      {IconC && <IconC className="mt-0.5 text-gray-400" />}
      <div className="flex flex-col">
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {label}
        </span>
        <span className="text-sm font-medium text-gray-800">
          {String(value)}
        </span>
      </div>
    </div>
  );
};

const SubSection: React.FC<{
  title: string;
  icon: IconComp;
  children: React.ReactNode;
}> = ({ title, icon: IconC, children }) => (
  <div className="mt-5 first:mt-0">
    <div className="mb-2 flex items-center gap-2">
      <IconC className="text-gray-400" />
      <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h5>
    </div>
    <div className="space-y-1.5 border-l-2 border-gray-100 pl-4">
      {children}
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2 text-center">
    <div className="text-lg font-bold text-gray-800">{value}</div>
    <div className="text-[11px] capitalize text-gray-500">{label}</div>
  </div>
);

const CrimeSearch: React.FC = () => {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [searching, setSearching] = useState(false);

  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [loadingDossier, setLoadingDossier] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Step 1 — debounced global search (person category)
  useEffect(() => {
    if (query.trim().length < 1) {
      setResults(null);
      setSearching(false);
      return;
    }

    setSearching(true);
    const timer = setTimeout(() => {
      globalSearch(query, { category: "person" })
        .then((data) => setResults(data))
        .catch((error) => errorHandler(error))
        .finally(() => setSearching(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Step 2 — open full crime record
  const openDossier = (personId: string) => {
    setActiveId(personId);
    setLoadingDossier(true);
    setDossier(null);
    fetchDossier({ suspect_id: personId })
      .then((data) => setDossier(data.dossiers?.[0] ?? null))
      .catch((error) => errorHandler(error))
      .finally(() => setLoadingDossier(false));
  };

  const personName =
    dossier?.suspect?.full_name || dossier?.asset_person?.full_name || "—";

  return (
    <div className="mx-auto max-w-7xl">
      {/* Hero / search header */}
      <div className="mb-6 overflow-hidden rounded-2xl bg-black p-6 shadow-lg md:p-8">
        <div className="flex items-center gap-3 text-white">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
            <Icon.Search className="text-lg" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{t("crime_search")}</h1>
            <p className="text-sm text-gray-300">
              {t("crime_search_select_hint")}
            </p>
          </div>
        </div>

        <div className="relative mt-5">
          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
            <Icon.Search />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            placeholder={t("crime_search_placeholder")}
            className="w-full rounded-xl border-0 bg-white py-3 pl-11 pr-4 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          />
          {searching && (
            <span className="absolute inset-y-0 right-4 flex items-center">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Result list */}
        <div className="lg:col-span-1">
          <div className="space-y-4">
            {!searching &&
              results &&
              results.groups.length === 0 &&
              query.trim().length > 0 && (
                <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-400">
                  {t("no_results")}
                </div>
              )}

            {searching && !results && (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-14 animate-pulse rounded-xl bg-gray-100"
                  />
                ))}
              </div>
            )}

            {results?.groups.map((group) => (
              <section key={group.type}>
                <div className="mb-2 flex items-center justify-between px-1">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.type_label}
                  </h4>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                    {group.count}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.results.map((item) => {
                    const active = item.id === activeId;
                    return (
                      <button
                        key={item.id}
                        onClick={() => openDossier(item.id)}
                        className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                          active
                            ? "border-black bg-black text-white shadow-md"
                            : "border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm"
                        }`}
                      >
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            active
                              ? "bg-white/15 text-white"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {getInitials(item.title)}
                        </div>
                        <div className="min-w-0">
                          <div
                            className={`truncate text-sm font-semibold ${
                              active ? "text-white" : "text-gray-800"
                            }`}
                          >
                            {item.title}
                          </div>
                          {item.subtitle && (
                            <div
                              className={`truncate text-xs ${
                                active ? "text-gray-300" : "text-gray-500"
                              }`}
                            >
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>

        {/* Dossier */}
        <div className="lg:col-span-2">
          {loadingDossier && (
            <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white">
              <span className="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-gray-700" />
              <span className="text-sm text-gray-400">
                {t("loading_record")}…
              </span>
            </div>
          )}

          {!loadingDossier && !dossier && (
            <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-white text-center">
              <Icon.User className="text-4xl text-gray-300" />
              <span className="max-w-xs text-sm text-gray-400">
                {t("crime_search_select_hint")}
              </span>
            </div>
          )}

          {!loadingDossier && dossier && (
            <div className="space-y-4">
              {/* Header card */}
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-4 bg-gray-50 p-5">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-black text-xl font-bold text-white">
                    {getInitials(personName)}
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-xl font-bold text-gray-900">
                      {personName}
                    </h2>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-500">
                      {dossier.suspect?.identity_card_number && (
                        <span className="inline-flex items-center gap-1.5">
                          <Icon.IdCard className="text-gray-400" />
                          {dossier.suspect.identity_card_number}
                        </span>
                      )}
                      {dossier.asset_person?.fayda_number && (
                        <span className="inline-flex items-center gap-1.5">
                          <Icon.IdCard className="text-gray-400" />
                          {dossier.asset_person.fayda_number}
                        </span>
                      )}
                      {dossier.suspect?.phone_number && (
                        <span className="inline-flex items-center gap-1.5">
                          <Icon.Phone className="text-gray-400" />
                          {dossier.suspect.phone_number}
                        </span>
                      )}
                      {dossier.suspect?.suspect_condition && (
                        <StatusBadge status={dossier.suspect.suspect_condition} />
                      )}
                    </div>
                  </div>
                </div>

                {dossier.totals && (
                  <div className="grid grid-cols-3 gap-2 border-t border-gray-100 p-4 sm:grid-cols-4 md:grid-cols-6">
                    {Object.entries(dossier.totals)
                      .filter(([, v]) => typeof v === "number")
                      .map(([k, v]) => (
                        <StatCard
                          key={k}
                          label={k.replace(/_/g, " ")}
                          value={String(v)}
                        />
                      ))}
                  </div>
                )}
              </div>

              {/* Cases */}
              {dossier.cases?.map((c, ci) => (
                <div
                  key={c.case?.id ?? ci}
                  className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
                    <h3 className="flex items-center gap-2 text-base font-semibold text-gray-800">
                      <Icon.Folder className="text-gray-400" />
                      {t("crime_case")} {c.case?.registration_number}
                    </h3>
                    <StatusBadge status={c.case?.status} />
                  </div>

                  {c.court_information?.length > 0 && (
                    <SubSection title={t("court_info")} icon={Icon.Building}>
                      {c.court_information.map((f, i) => (
                        <div key={f.id ?? i} className="text-sm text-gray-700">
                          {f.court_type} ·{" "}
                          <span className="text-gray-500">
                            {f.court_registration_number}
                          </span>
                        </div>
                      ))}
                    </SubSection>
                  )}

                  {c.charges?.length > 0 && (
                    <SubSection title={t("charge")} icon={Icon.File}>
                      {c.charges.map((ch, i) => (
                        <div key={ch.id ?? i} className="text-sm text-gray-700">
                          <span className="font-medium text-gray-800">
                            {t("count")} {ch.count_number}:
                          </span>{" "}
                          {ch.crime_type}{" "}
                          {ch.articles_of_the_charge && (
                            <span className="text-gray-500">
                              ({ch.articles_of_the_charge})
                            </span>
                          )}
                        </div>
                      ))}
                    </SubSection>
                  )}

                  {c.court_hearings?.length > 0 && (
                    <SubSection title={t("hearings")} icon={Icon.Scale}>
                      {c.court_hearings.map((h, i) => (
                        <div key={h.id ?? i} className="text-sm text-gray-700">
                          <span className="font-medium text-gray-500">
                            {h.report_date}
                          </span>
                          {" — "}
                          {h.summary}
                        </div>
                      ))}
                    </SubSection>
                  )}

                  {c.sentence_decisions?.length > 0 && (
                    <SubSection title={t("sentence")} icon={Icon.Gavel}>
                      {c.sentence_decisions.map((v, i) => (
                        <div
                          key={v.id ?? i}
                          className="inline-flex items-center gap-2 text-sm text-gray-700"
                        >
                          <StatusBadge status={v.court_decision} />
                          <span className="font-medium">
                            {v.court_decision_amount}
                          </span>
                        </div>
                      ))}
                    </SubSection>
                  )}

                  {c.appeals?.length > 0 && (
                    <SubSection title={t("appeal")} icon={Icon.Gavel}>
                      {c.appeals.map((a, i) => (
                        <div
                          key={a.id ?? i}
                          className="rounded-lg bg-gray-50 p-2.5 text-sm text-gray-700"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium capitalize">
                              {String(a.kind).replace(/_/g, " ")}
                            </span>
                            <span className="text-gray-500">
                              {a.court_appeal_number}
                            </span>
                            <StatusBadge status={a.status} />
                          </div>
                          {a.decisions?.map((d, di) => (
                            <div
                              key={d.id ?? di}
                              className="mt-1 pl-3 text-gray-600"
                            >
                              ↳ {d.amended_court_decision}
                            </div>
                          ))}
                        </div>
                      ))}
                    </SubSection>
                  )}
                </div>
              ))}

              {/* Bail */}
              {dossier.bail?.length > 0 && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="flex items-center gap-2 border-b border-gray-100 pb-3 text-base font-semibold text-gray-800">
                    <Icon.Scale className="text-gray-400" />
                    {t("bail_right")}
                  </h3>
                  <div className="mt-3 space-y-2">
                    {dossier.bail.map((b, i) => (
                      <div
                        key={b.id ?? i}
                        className="flex flex-wrap items-center gap-2 text-sm text-gray-700"
                      >
                        <span className="font-medium">{b.reference_number}</span>
                        {(b.hearing_decisions || []).map(
                          (hd: any, hi: number) =>
                            hd.final_outcome ? (
                              <StatusBadge key={hi} status={hd.final_outcome} />
                            ) : null,
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Asset recovery */}
              {dossier.asset_recovery?.referrals?.length ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="flex items-center gap-2 border-b border-gray-100 pb-3 text-base font-semibold text-gray-800">
                    <Icon.Coins className="text-gray-400" />
                    {t("asset_recovery")}
                  </h3>
                  <div className="mt-3 space-y-3">
                    {dossier.asset_recovery.referrals.map((r, i) => (
                      <div
                        key={r.referral?.id ?? i}
                        className="rounded-lg bg-gray-50 p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-800">
                            {r.referral?.referral_id}
                          </span>
                          <StatusBadge status={r.referral?.action_status} />
                        </div>
                        {r.assets?.length > 0 && (
                          <div className="mt-1 text-gray-600">
                            {t("assets")}:{" "}
                            {r.assets
                              .map((a: any) => a.asset_number)
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrimeSearch;
